import type { Official } from './apiClient';

export type LifecycleBadgeTone = 'archived' | 'inactive' | 'vying' | null;

export interface OfficialLifecycleSummary {
  badgeLabel: string | null;
  badgeTone: LifecycleBadgeTone;
  detailLabel: string | null;
  ctaLabel: string;
}

const ARCHIVE_REASON_LABELS: Record<string, string> = {
  replaced_after_election: 'Replaced after election',
  replaced_in_latest_roster: 'Replaced in latest roster',
  missing_from_latest_roster: 'Missing from latest roster',
};

const RACE_STAGE_LABELS: Record<string, string> = {
  primary: 'Primary',
  general: 'General',
};

function humanizeArchiveReason(reason: string | null | undefined): string | null {
  if (!reason) return null;
  const label = ARCHIVE_REASON_LABELS[reason] ?? reason.replace(/_/g, ' ');
  return label.charAt(0).toUpperCase() + label.slice(1);
}

export function formatOfficialStatusLabel(official: Official): string {
  if (official.lifecycle_status === 'archived') return 'Archived';
  if (official.lifecycle_status === 'vying') {
    const raceStageLabel = official.race_stage ? RACE_STAGE_LABELS[official.race_stage] ?? null : null;
    return raceStageLabel ? `Vying (${raceStageLabel})` : 'Vying';
  }
  if (official.lifecycle_status === 'inactive' || !official.is_active) return 'Inactive';
  return 'Active';
}

export function buildOfficialLifecycleSummary(official: Official): OfficialLifecycleSummary {
  if (official.lifecycle_status === 'archived') {
    const parts: string[] = [];
    if (official.archived_at) {
      parts.push(`Archived ${new Date(official.archived_at).toLocaleDateString('en-US', { timeZone: 'UTC' })}`);
    } else {
      parts.push('Archived record');
    }
    const reasonLabel = humanizeArchiveReason(official.archive_reason);
    if (reasonLabel) parts.push(reasonLabel);
    if (official.replaced_by_official_id) parts.push('Successor recorded');
    return {
      badgeLabel: 'Archived',
      badgeTone: 'archived',
      detailLabel: parts.join(' • '),
      ctaLabel: 'View Archive',
    };
  }

  if (official.lifecycle_status === 'vying') {
    if (official.race_stage === 'primary') {
      return {
        badgeLabel: 'Primary',
        badgeTone: 'vying',
        detailLabel: 'Currently in a primary election race',
        ctaLabel: 'View Profile',
      };
    }
    if (official.race_stage === 'general') {
      return {
        badgeLabel: 'General',
        badgeTone: 'vying',
        detailLabel: 'Currently in a general election race',
        ctaLabel: 'View Profile',
      };
    }
    return {
      badgeLabel: 'Vying',
      badgeTone: 'vying',
      detailLabel: 'Currently vying for office',
      ctaLabel: 'View Profile',
    };
  }

  if (official.lifecycle_status === 'inactive') {
    return {
      badgeLabel: 'Inactive',
      badgeTone: 'inactive',
      detailLabel: 'No longer marked as active',
      ctaLabel: 'View Profile',
    };
  }

  return {
    badgeLabel: null,
    badgeTone: null,
    detailLabel: null,
    ctaLabel: 'View Dossier',
  };
}
